import {BodyContainer} from '@coolcolduk/ui-mui';
import {Menu, MenuItem, Tab, Tabs} from '@mui/material';
import {PropsWithChildren, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import PageHeader from '../components/PageHeader';
import {PrivateRouteProps} from '../config/routeType';
import {adminTabs} from '../pages/admin/adminTabs';
import {filterTabs} from '../pages/admin/filterTabs';

export default function AdminLayout({user, children}: PropsWithChildren<PrivateRouteProps>) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<{element: HTMLElement; group: string} | null>(null);

  // Filter tabs based on user permissions
  const accessibleTabs = useMemo(() => filterTabs(adminTabs, user?.roles), [user?.roles]);

  // Filter out parameterized routes from tab navigation (only show base routes)
  const navigationTabs = useMemo(() => accessibleTabs.filter((tab) => !tab.path.includes(':')), [accessibleTabs]);

  // Group tabs by group property
  const {groupedTabs, ungroupedTabs} = useMemo(() => {
    const grouped = new Map<string, typeof navigationTabs>();
    const ungrouped: typeof navigationTabs = [];

    navigationTabs.forEach((tab) => {
      if (tab.group) {
        if (!grouped.has(tab.group)) {
          grouped.set(tab.group, []);
        }
        grouped.get(tab.group)!.push(tab);
      } else {
        ungrouped.push(tab);
      }
    });

    return {groupedTabs: Array.from(grouped.entries()), ungroupedTabs: ungrouped};
  }, [navigationTabs]);

  // If user has no access to any admin pages, don't render
  if (navigationTabs.length === 0) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    // If it's a group name, don't navigate (menu will be opened by onClick)
    if (groupedTabs.some(([groupName]) => groupName === newValue)) {
      return;
    }
    navigate(newValue);
  };

  const handleGroupTabClick = (event: React.MouseEvent<HTMLElement>, groupName: string) => {
    event.preventDefault();
    setMenuAnchor({element: event.currentTarget, group: groupName});
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  // Determine which tab is active - check if pathname starts with tab path
  const getActiveTabValue = () => {
    // Check if pathname matches any tab path exactly or starts with it (for parameterized routes)
    const matchingTab = navigationTabs.find((tab) => {
      if (location.pathname === tab.path) return true;
      // For study tab, also match parameterized routes
      if (tab.path === '/admin/study' && location.pathname.startsWith('/admin/study/')) return true;
      // For module tab, also match parameterized routes
      if (tab.path === '/admin/module' && location.pathname.startsWith('/admin/module/')) return true;
      // For questionReview tab, also match parameterized routes
      if (tab.path === '/admin/questionReview' && location.pathname.startsWith('/admin/questionReview/')) return true;
      // For question tab, also match parameterized routes
      if (tab.path === '/admin/question' && location.pathname.startsWith('/admin/question/')) return true;
      // For seedQuestions tab, also match parameterized routes
      if (tab.path === '/admin/seedQuestions' && location.pathname.startsWith('/admin/seedQuestions')) return true;
      return false;
    });

    // If matching tab is in a group, return the group name
    if (matchingTab?.group) {
      return matchingTab.group;
    }

    // Default to first accessible tab if no match found
    return matchingTab?.path || navigationTabs[0]?.path || '/admin';
  };

  const activeTabValue = getActiveTabValue();

  return (
    <>
      <PageHeader title="Admin" />
      <BodyContainer>
        <Tabs
          onChange={handleTabChange}
          aria-label="admin tabs"
          value={activeTabValue}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            maxWidth: '100vw',
            mb: 3,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              minWidth: {xs: 'auto', sm: 72},
              fontSize: {xs: '0.875rem', sm: '1rem'},
              minHeight: {xs: 40, sm: 48},
            },
          }}
        >
          {ungroupedTabs.map((tab) => (
            <Tab key={tab.label} label={tab.label} value={tab.path} />
          ))}
          {groupedTabs.map(([groupName]) => (
            <Tab
              key={groupName}
              label={groupName}
              value={groupName}
              onClick={(e) => handleGroupTabClick(e, groupName)}
              aria-haspopup="true"
            />
          ))}
        </Tabs>
        {groupedTabs.map(([groupName, tabs]) => (
          <Menu
            key={groupName}
            anchorEl={menuAnchor?.group === groupName ? menuAnchor.element : null}
            open={menuAnchor?.group === groupName}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': `group-tab-${groupName}`,
            }}
          >
            {tabs.map((tab) => (
              <MenuItem key={tab.path} onClick={() => handleMenuItemClick(tab.path)}>
                {tab.label}
              </MenuItem>
            ))}
          </Menu>
        ))}
        {children}
      </BodyContainer>
    </>
  );
}
